import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as numbers from "common/numbers";
import Component from "components/Component";
import * as labels from "config.labels";
import { useSelector } from "react-redux";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import { selectRateAdequacyComparison } from "store/selectors/pricing/rateAdequacySelectors";

const BLANK = "—";

const isNumber = (value) => typeof value === "number" && Number.isFinite(value);

const formatPercentage = numbers.percentage(2);

export const formatRatioCell = (value) =>
  isNumber(value) ? formatPercentage(value) : BLANK;

export const formatAdequacyCell = (value) =>
  isNumber(value) ? value.toFixed(2) : BLANK;

export const formatRateChangeCell = (value) => {
  if (!isNumber(value)) {
    return BLANK;
  }
  const sign = value > 0 ? "+" : "";
  return `${sign}${formatPercentage(value)}`;
};

// Year-on-year movement of a ratio row, in percentage points.
export const formatRatioDeltaCell = (current, prior) =>
  isNumber(current) && isNumber(prior)
    ? formatRateChangeCell(current - prior)
    : BLANK;

// Year-on-year movement of the adequacy ratio itself.
export const formatAdequacyDeltaCell = (current, prior) => {
  if (!isNumber(current) || !isNumber(prior)) {
    return BLANK;
  }
  const delta = current - prior;
  const sign = delta > 0 ? "+" : "";
  return `${sign}${delta.toFixed(2)}`;
};

// Whether a movement is good news: costs and the combined ratio are better
// when they fall, adequacy is better when it rises, the target is neutral.
export const deltaTone = (current, prior, goodWhen) => {
  if (!isNumber(current) || !isNumber(prior) || goodWhen == null) {
    return null;
  }
  const delta = current - prior;
  if (delta === 0) {
    return null;
  }
  const isUp = delta > 0;
  return isUp === (goodWhen === "up") ? "good" : "bad";
};

const DeltaCell = ({ value, tone }) => {
  const theme = useTheme();
  return (
    <Box
      component={"span"}
      data-testid={tone ? `rate-adequacy-delta-${tone}` : undefined}
      sx={{
        borderRadius: 2,
        px: tone ? "5px" : 0,
        lineHeight: "1.4rem",
        ...(tone === "good" && {
          backgroundColor:
            theme.brand?.green?.[100] ?? theme.palette.success.light,
          color: theme.brand?.green?.[900] ?? theme.palette.success.dark,
        }),
        ...(tone === "bad" && {
          backgroundColor: theme.brand?.red?.[100] ?? theme.palette.error.light,
          color: theme.brand?.red?.[800] ?? theme.palette.error.dark,
        }),
      }}
    >
      {value}
    </Box>
  );
};

const useLabels = () => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  return {
    title: translateLabel(labels.RATE_ADEQUACY_TITLE, {
      default: "Rate Adequacy",
    }),
    currentYear: translateLabel(labels.RATE_ADEQUACY_CURRENT_YEAR, {
      default: "Current Year",
    }),
    previousYear: translateLabel(labels.RATE_ADEQUACY_PREVIOUS_YEAR, {
      default: "Previous Year",
    }),
    change: translateLabel(labels.RATE_ADEQUACY_CHANGE, {
      default: "Change",
    }),
    lossPick: translateLabel(labels.RATE_ADEQUACY_LOSS_PICK, {
      default: "Loss Pick",
    }),
    expectedCommission: translateLabel(
      labels.RATE_ADEQUACY_EXPECTED_COMMISSION,
      { default: "Expected Commission" }
    ),
    brokerage: translateLabel(labels.RATE_ADEQUACY_BROKERAGE, {
      default: "Brokerage",
    }),
    expenses: translateLabel(labels.RATE_ADEQUACY_EXPENSES, {
      default: "Expenses",
    }),
    combinedRatio: translateLabel(labels.RATE_ADEQUACY_COMBINED_RATIO, {
      default: "Combined Ratio",
    }),
    targetCombinedRatio: translateLabel(
      labels.RATE_ADEQUACY_TARGET_COMBINED_RATIO,
      { default: "Target Combined Ratio" }
    ),
    adequacy: translateLabel(labels.RATE_ADEQUACY_ADEQUACY, {
      default: "Adequacy",
    }),
    rateChange: translateLabel(labels.RATE_ADEQUACY_RATE_CHANGE, {
      default: "Rate Change",
    }),
    noPrior: translateLabel(labels.RATE_ADEQUACY_NO_PRIOR, {
      default: "No prior submission linked",
    }),
    noTarget: translateLabel(labels.RATE_ADEQUACY_NO_TARGET, {
      default: "No Initial Expected Loss Ratio entered on Loss Development",
    }),
  };
};

const buildRows = (text, comparison) => {
  const { current, prior, rateChange } = comparison ?? {};
  const ratioRow = (id, label, goodWhen) => ({
    id,
    label,
    current: formatRatioCell(current?.[id]),
    prior: formatRatioCell(prior?.[id]),
    delta: formatRatioDeltaCell(current?.[id], prior?.[id]),
    tone: deltaTone(current?.[id], prior?.[id], goodWhen),
  });
  return [
    ratioRow("lossPick", text.lossPick, "down"),
    ratioRow("expectedCommission", text.expectedCommission, "down"),
    ratioRow("brokerage", text.brokerage, "down"),
    ratioRow("expenses", text.expenses, "down"),
    ratioRow("combinedRatio", text.combinedRatio, "down"),
    ratioRow("targetCombinedRatio", text.targetCombinedRatio, null),
    {
      id: "adequacy",
      label: text.adequacy,
      current: formatAdequacyCell(current?.adequacy),
      prior: formatAdequacyCell(prior?.adequacy),
      delta: formatAdequacyDeltaCell(current?.adequacy, prior?.adequacy),
      tone: deltaTone(current?.adequacy, prior?.adequacy, "up"),
    },
    {
      id: "rateChange",
      label: text.rateChange,
      current: formatRateChangeCell(rateChange),
      prior: "",
      delta: "",
      tone: null,
    },
  ];
};

/*
 * The table as the Client drew it: eight rows by Current Year | Previous
 * Year, plus the year-on-year Change the reviewer asked for.
 * Pure presentation over a `comparison` from selectRateAdequacyComparison,
 * so the Summary page can print it without the dashboard chrome.
 */
export const RateAdequacyTable = ({ comparison }) => {
  const text = useLabels();
  const rows = buildRows(text, comparison);
  const current = comparison?.current;
  const hasPrior = !!comparison?.hasPrior;
  const isMissingTarget = current != null && current.targetLossRatio == null;

  return (
    <Stack direction={"column"} spacing={1}>
      <Table size={"small"} aria-label={text.title}>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell align={"right"}>{text.currentYear}</TableCell>
            <TableCell align={"right"}>{text.previousYear}</TableCell>
            <TableCell align={"right"}>{text.change}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} data-testid={`rate-adequacy-row-${row.id}`}>
              <TableCell component={"th"} scope={"row"}>
                {row.label}
              </TableCell>
              <TableCell align={"right"}>{row.current}</TableCell>
              <TableCell align={"right"}>{row.prior}</TableCell>
              <TableCell align={"right"}>
                <DeltaCell value={row.delta} tone={row.tone} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!hasPrior && (
        <Typography
          variant={"body2"}
          color={"text.secondary"}
          data-testid={"rate-adequacy-no-prior"}
        >
          {text.noPrior}
        </Typography>
      )}
      {isMissingTarget && (
        <Typography
          variant={"body2"}
          color={"text.secondary"}
          data-testid={"rate-adequacy-no-target"}
        >
          {text.noTarget}
        </Typography>
      )}
    </Stack>
  );
};

/*
 * Reads the comparison from the store. Any `componentProps` (for example a
 * Summary page width) pass through to the card.
 */
export const RateAdequacyCard = ({ title, ...componentProps }) => {
  const text = useLabels();
  const comparison = useSelector(selectRateAdequacyComparison);

  return (
    <Component
      title={title ?? text.title}
      md_width={12}
      lg_width={6}
      {...componentProps}
    >
      <RateAdequacyTable comparison={comparison} />
    </Component>
  );
};

const RateAdequacy = () => <RateAdequacyCard />;

export default RateAdequacy;
